/**
 * @file SSOLinkRedirect.js
 * @description Landing route for SSO links opened from outside the app (e.g. notification emails
 * and SMS). The login URL is requested on arrival, so it is always fresh and issued for the
 * signed-in recipient; unauthenticated visitors are sent through login first and returned here.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from '../ui/spinners/Spinner';
import { ROUTES } from '../../constants/routes';
import { requestSSOLoginUrl } from '../../utils/resolveSSOLinks';
import {
    showSSOGenericError,
    showSSOLoginValidationWarning,
} from '../../utils/ssoLinkClickHandler';
import styles from './SSOLinkRedirect.module.scss';

const TENANT_SSO_ID_PATTERN = /^\d+$/;

async function openSSOLogin(tenantSSOId) {
    if (!TENANT_SSO_ID_PATTERN.test(tenantSSOId || '')) {
        showSSOLoginValidationWarning();
        return false;
    }

    try {
        const loginUrl = await requestSSOLoginUrl(tenantSSOId);
        if (!loginUrl) {
            showSSOLoginValidationWarning();
            return false;
        }

        window.location.replace(loginUrl);
        return true;
    } catch (error) {
        console.error('Error opening SSO link:', error);
        showSSOGenericError();
        return false;
    }
}

function SSOLinkRedirect() {
    const { tenantSSOId } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        let isCurrent = true;

        openSSOLogin(tenantSSOId).then(hasRedirected => {
            if (isCurrent && !hasRedirected) {
                navigate(ROUTES.ROOT, { replace: true });
            }
        });

        return () => {
            isCurrent = false;
        };
    }, [tenantSSOId, navigate]);

    return (
        <div className={styles.container}>
            <Spinner className={styles.spinner} />
        </div>
    );
}

export default SSOLinkRedirect;
